<template>
  <div class="wizard-bill-subform">
    <!-- Header -->
    <div
      class="subform-header d-flex align-items-center justify-content-between p-3 cursor-pointer"
      @click="expanded = !expanded"
    >
      <div class="d-flex align-items-center">
        <i :class="expanded ? 'fas fa-chevron-down' : 'fas fa-chevron-right'" class="mr-2 text-muted"></i>
        <div>
          <span class="font-weight-bold text-primary">{{ item.returnBillNumber }}</span>
          <span v-if="item.originalBillNumber" class="text-muted ml-2">
            (HĐ gốc: {{ item.originalBillNumber }})
          </span>
          <span class="ml-2 text-muted" style="font-size:0.8rem">{{ flowLabel }}</span>
        </div>
      </div>
      <div class="d-flex align-items-center">
        <span v-if="previewed" class="badge badge-success mr-2">
          <i class="fas fa-check mr-1"></i> Đã xem trước
        </span>
        <span v-else class="badge badge-secondary mr-2">Chưa xem trước</span>
      </div>
    </div>

    <div v-if="expanded" class="subform-body p-3">
      <!-- Block A: Return Bill Info (read-only) -->
      <div class="section-title mb-2"><strong>A. Thông tin chứng từ trả hàng</strong></div>
      <b-row class="mb-3">
        <b-col cols="3">
          <div class="text-muted" style="font-size:0.8rem">Số chứng từ</div>
          <div class="font-weight-bold">{{ item.returnBillNumber }}</div>
        </b-col>
        <b-col cols="3">
          <div class="text-muted" style="font-size:0.8rem">Ngày trả hàng</div>
          <div>{{ formatDate(item.returnDate) }}</div>
        </b-col>
        <b-col cols="3">
          <div class="text-muted" style="font-size:0.8rem">Cửa hàng</div>
          <div>{{ item.storeName || '—' }}</div>
        </b-col>
        <b-col cols="3">
          <div class="text-muted" style="font-size:0.8rem">Tổng tiền</div>
          <div class="text-danger font-weight-bold">{{ formatCurrency(item.totalAmount) }}</div>
        </b-col>
        <b-col cols="3" class="mt-2">
          <div class="text-muted" style="font-size:0.8rem">HĐ gốc</div>
          <div>{{ item.originalBillNumber || '—' }}</div>
        </b-col>
        <b-col cols="3" class="mt-2">
          <div class="text-muted" style="font-size:0.8rem">Nguồn</div>
          <div>{{ displaySourceTypeName }}</div>
        </b-col>
        <b-col cols="3" class="mt-2">
          <div class="text-muted" style="font-size:0.8rem">Luồng</div>
          <span class="badge" :class="item.providerFlow === 'POS' ? 'badge-dark' : 'badge-info'">
            {{ item.providerFlow }}
          </span>
        </b-col>
      </b-row>
      <hr class="my-2" />

      <!-- Block B: Buyer Info (chỉ hiển thị — lấy từ hóa đơn gốc, không cho sửa) -->
      <div class="section-title mb-2">
        <strong>B. Thông tin người mua</strong>
        <small class="text-muted ml-2">(lấy theo hóa đơn gốc, không chỉnh sửa)</small>
      </div>
      <b-row>
        <b-col cols="6">
          <b-form-group label="Loại người mua" label-cols="4">
            <b-form-select v-model="form.buyerType" :options="buyerTypeOptions" size="sm" disabled />
          </b-form-group>
        </b-col>
        <b-col cols="6">
          <b-form-group label="PT thanh toán" label-cols="4">
            <b-form-input v-model="form.paymentMethod" size="sm" placeholder="Cấn trừ công nợ" disabled />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row v-if="form.buyerType === 'ENTERPRISE'">
        <b-col cols="6">
          <b-form-group label="Tên đơn vị *" label-cols="4">
            <b-form-input v-model="form.buyerLegalName" size="sm" :state="validateField('buyerLegalName')" disabled />
          </b-form-group>
        </b-col>
        <b-col cols="6">
          <b-form-group label="MST *" label-cols="4">
            <b-form-input v-model="form.buyerTaxCode" size="sm" :state="validateField('buyerTaxCode')" disabled />
          </b-form-group>
        </b-col>
        <b-col cols="12">
          <b-form-group label="Địa chỉ *" label-cols="2">
            <b-form-input v-model="form.buyerAddress" size="sm" :state="validateField('buyerAddress')" disabled />
          </b-form-group>
        </b-col>
        <b-col cols="6">
          <b-form-group label="Người mua hàng" label-cols="4">
            <b-form-input
              v-model="form.buyerName"
              size="sm"
              placeholder="—"
              disabled
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row v-if="form.buyerType === 'INDIVIDUAL'">
        <b-col cols="6">
          <b-form-group label-cols="4">
            <template #label>
              Họ tên người mua <span class="text-danger">*</span>
            </template>
            <b-form-input v-model="form.buyerName" size="sm" :state="validateField('buyerName')" disabled />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="6">
          <b-form-group label="Điện thoại" label-cols="4">
            <b-form-input v-model="form.buyerPhone" size="sm" disabled />
          </b-form-group>
        </b-col>
        <b-col cols="6">
          <b-form-group label="Email" label-cols="4">
            <b-form-input v-model="form.buyerEmail" size="sm" disabled />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Block B: Display Reference -->
      <div class="section-title mt-3 mb-2"><strong>B. Tham chiếu hóa đơn gốc (hiển thị)</strong></div>
      <b-row>
        <b-col cols="3">
          <b-form-group label="Mẫu số" label-cols="4">
            <b-form-input v-model="form.displayOriginalInvoicePattern" size="sm" />
          </b-form-group>
        </b-col>
        <b-col cols="3">
          <b-form-group label="Ký hiệu" label-cols="4">
            <b-form-input v-model="form.displayOriginalInvoiceSerial" size="sm" />
          </b-form-group>
        </b-col>
        <b-col cols="3">
          <b-form-group label-cols="4">
            <template #label>
              Số HĐ <span class="text-danger">*</span>
            </template>
            <b-form-input v-model.trim="form.displayOriginalInvoiceNo" size="sm" :state="validateField('displayOriginalInvoiceNo')" />
          </b-form-group>
        </b-col>
        <b-col cols="3">
          <b-form-group label-cols="4">
            <template #label>
              Ngày HĐ <span class="text-danger">*</span>
            </template>
            <b-form-input v-model="form.displayOriginalInvoiceDate" size="sm" type="date" :state="validateField('displayOriginalInvoiceDate')" />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Block C: Reason -->
      <div class="section-title mt-3 mb-2"><strong>C. Lý do điều chỉnh</strong></div>
      <b-form-textarea v-model="form.reason" rows="2" size="sm" placeholder="Lý do điều chỉnh..." />

      <!-- Block D: Preview -->
      <div class="mt-3 d-flex align-items-center">
        <b-button size="sm" variant="outline-primary" :disabled="!canPreview || isPreviewLoading" @click="onPreview">
          <i v-if="isPreviewLoading" class="fas fa-spinner fa-spin mr-1"></i>
          <i v-else class="fas fa-eye mr-1"></i>
          Xem trước điều chỉnh
        </b-button>
        <span v-if="!canPreview" class="text-danger ml-2" style="font-size:0.8rem">Vui lòng điền đầy đủ thông tin bắt buộc (*)</span>
      </div>

      <div v-if="previewData" class="mt-3 preview-box">
        <div class="section-title mb-2"><strong>D. Kết quả xem trước (số âm = giảm)</strong></div>
        <table class="table table-sm table-bordered mb-2">
          <thead class="thead-light">
            <tr>
              <th>Tên hàng hóa, dịch vụ</th>
              <th class="text-center">ĐVT</th>
              <th class="text-right">SL điều chỉnh</th>
              <th class="text-right">Đơn giá</th>
              <th class="text-right">Chiết khấu ĐC</th>
              <th class="text-center">Thuế suất</th>
              <th class="text-right">Thành tiền chưa thuế ĐC</th>
              <th class="text-right">Tiền thuế ĐC</th>
              <th class="text-right">Tổng ĐC</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(it, idx) in previewData.items" :key="idx">
              <td>{{ it.productName }}</td>
              <td class="text-center">{{ it.unitName || '—' }}</td>
              <td class="text-right text-danger">{{ it.quantityAdjustment }}</td>
              <td class="text-right">{{ formatCurrency(it.productPrice) }}</td>
              <td class="text-right text-danger">{{ formatCurrency(it.discountAmount) }}</td>
              <td class="text-center">{{ formatTaxRate(it.vatRate) }}</td>
              <td class="text-right text-danger">{{ formatCurrency(it.amountBeforeTaxAdjustment) }}</td>
              <td class="text-right text-danger">{{ formatCurrency(it.taxAmountAdjustment) }}</td>
              <td class="text-right text-danger">{{ formatCurrency(it.totalAmountAdjustment) }}</td>
            </tr>
          </tbody>
          <tfoot class="font-weight-bold">
            <tr>
              <td colspan="6" class="text-right">Tổng cộng:</td>
              <td class="text-right text-danger">{{ formatCurrency(previewData.totals.amountBeforeTax) }}</td>
              <td class="text-right text-danger">{{ formatCurrency(previewData.totals.taxAmount) }}</td>
              <td class="text-right text-danger">{{ formatCurrency(previewData.totals.totalAmount) }}</td>
            </tr>
          </tfoot>
        </table>
        <div v-if="previewData.disclaimer" class="text-muted" style="font-size:0.8rem">
          <i class="fas fa-info-circle mr-1"></i>{{ previewData.disclaimer }}
        </div>
      </div>

      <div v-if="previewError" class="alert alert-danger mt-2 py-2">
        <i class="fas fa-exclamation-circle mr-1"></i>{{ previewError }}
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from '@/core/services/api.service';
import { normalizeEmailList } from '@/utils/einvoice.enum';
import { getAdjustmentSourceTypeLabelVi } from '../constants/sourceTypeLabels';

function formatCurrency(value) {
  return new Intl.NumberFormat('vi-VN').format(Number(value) || 0);
}

function toDateInputValue(v) {
  if (v == null || v === '') return '';
  if (v instanceof Date && !Number.isNaN(v.getTime())) return v.toISOString().slice(0, 10);
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

export default {
  name: 'WizardBillSubform',
  props: {
    item: { type: Object, required: true },
    wizardId: { type: [Number, String], required: true },
  },
  data() {
    const ci = this.item.customerInfoPrefill || {};
    const dr = this.item.displayReferencePrefill || {};
    return {
      expanded: true,
      isPreviewLoading: false,
      previewData: null,
      previewError: null,
      previewed: false,
      form: {
        buyerType: ci.buyerType || 'INDIVIDUAL',
        buyerName: ci.buyerName || '',
        buyerLegalName: ci.buyerLegalName || '',
        buyerTaxCode: ci.buyerTaxCode || '',
        buyerAddress: ci.buyerAddress || '',
        buyerPhone: ci.buyerPhone || '',
        buyerEmail: ci.buyerEmail || '',
        paymentMethod: ci.paymentMethod || 'Cấn trừ công nợ',
        displayOriginalInvoicePattern: dr.pattern?.trim() || '',
        displayOriginalInvoiceSerial: dr.serial?.trim() || '',
        displayOriginalInvoiceNo: dr.invoiceNo?.trim() || '',
        displayOriginalInvoiceDate: toDateInputValue(dr.invoiceDate),
        reason: this.item.reasonPrefill || '',
      },
    };
  },
  computed: {
    buyerTypeOptions() {
      return [
        { value: 'INDIVIDUAL', text: 'Cá nhân' },
        { value: 'ENTERPRISE', text: 'Doanh nghiệp' },
      ];
    },
    flowLabel() {
      return this.item.providerFlow === 'POS' ? '[POS]' : '[Cổng thuế]';
    },
    displaySourceTypeName() {
      return (
        this.item.sourceTypeName
        || getAdjustmentSourceTypeLabelVi(this.item.sourceType)
        || '—'
      );
    },
    canPreview() {
      const d = this.form;
      if (!d.displayOriginalInvoicePattern?.trim()) return false;
      if (!d.displayOriginalInvoiceSerial?.trim()) return false;
      if (!d.displayOriginalInvoiceNo?.trim() || !d.displayOriginalInvoiceDate) return false;
      if (d.buyerType === 'ENTERPRISE') {
        return !!(d.buyerLegalName?.trim() && d.buyerTaxCode?.trim() && d.buyerAddress?.trim());
      }
      return !!d.buyerName?.trim();
    },
    formData() {
      return {
        returnBillId: this.item.returnBillId,
        buyerInfo: {
          buyerType: this.form.buyerType,
          buyerName: this.form.buyerName,
          buyerLegalName: this.form.buyerLegalName,
          buyerTaxCode: this.form.buyerTaxCode,
          buyerAddress: this.form.buyerAddress,
          buyerPhone: this.form.buyerPhone,
          buyerEmail: normalizeEmailList(this.form.buyerEmail),
          paymentMethod: this.form.paymentMethod,
        },
        displayReference: {
          pattern: this.form.displayOriginalInvoicePattern,
          serial: this.form.displayOriginalInvoiceSerial,
          invoiceNo: this.form.displayOriginalInvoiceNo,
          invoiceDate: this.form.displayOriginalInvoiceDate,
        },
        reason: this.form.reason,
        previewed: this.previewed,
      };
    },
  },
  watch: {
    formData: {
      deep: true,
      immediate: true,
      handler(val) {
        this.$emit('form-change', val);
      },
    },
    canPreview(now, prev) {
      if (now && prev === false && !this.isPreviewLoading) {
        this.$nextTick(() => this.requestPreviewIfReady());
      }
    },
  },
  methods: {
    formatCurrency,
    formatDate: (d) => (d ? new Date(d).toLocaleDateString('vi-VN') : '—'),
    formatTaxRate(taxRate) {
      if (taxRate === -1) return 'KCT';
      if (taxRate === -2) return 'KKK';
      if (taxRate === -9) return 'XK';
      return `${Number(taxRate) || 0}%`;
    },
    async requestPreviewIfReady() {
      if (this.previewed || this.isPreviewLoading) return;
      if (!this.canPreview) return;
      await this.onPreview();
    },
    validateField(field) {
      const required = {
        ENTERPRISE: ['buyerLegalName', 'buyerTaxCode', 'buyerAddress'],
        INDIVIDUAL: ['buyerName'],
        common: ['displayOriginalInvoiceNo', 'displayOriginalInvoiceDate'],
      };
      const allRequired = [
        ...required.common,
        ...(required[this.form.buyerType] || []),
      ];
      if (!allRequired.includes(field)) return null;
      return this.form[field] ? null : false;
    },
    async onPreview() {
      this.isPreviewLoading = true;
      this.previewError = null;
      try {
        const { data } = await ApiService.post(
          `v1/invoice-adjustment-wizard/items/${this.item.returnBillId}/preview`,
          {
            wizardId: this.wizardId,
            buyerInfo: this.formData.buyerInfo,
            displayReference: this.formData.displayReference,
            reason: this.form.reason,
          },
        );
        const result = data.data || data;
        this.previewData = {
          items: result.items || result.previewItems || [],
          totals: result.totals || result.previewTotals || {},
          disclaimer: result.disclaimer,
        };
        this.previewed = true;
        this.$emit('form-change', this.formData);
      } catch (err) {
        this.previewError = err?.response?.data?.message || 'Lỗi khi xem trước. Vui lòng kiểm tra thông tin.';
      } finally {
        this.isPreviewLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.wizard-bill-subform {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin-bottom: 12px;
  overflow: hidden;

  .subform-header {
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    &:hover { background: #f0f4f8; }
  }

  .section-title { color: #374151; }

  .preview-box {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 12px;
  }
}
</style>
